/**
 * Compile the shaders before anybody is looking.
 *
 * A material's program is built the first time it is drawn, and the cost lands
 * entirely on that frame. Most of them are cheap. The hostile shell is not: it
 * is a physical material with iridescence and a clearcoat, and the first
 * enemy to appear on screen was costing half a second — measured, on a fast
 * machine, at the exact moment something walks into the room to kill you.
 *
 * The pickups had the same shape of problem from the other direction. Every
 * one of them carried its own point light, and the number of lights the
 * renderer can see is part of every material's program key, so a pickup
 * dropping or being collected invalidated every shader in the game. That light
 * is gone; this handles what is left.
 *
 * So everything that will ever be drawn is drawn once, off-screen, while the
 * floor is loading and a hitch is invisible. It has to happen after the world
 * is in the scene and the lights are set, because the light count is part of
 * what is being compiled against.
 */

import * as THREE from 'three';
import { makeBody } from './bodies.ts';
import { ENEMY_TYPES } from './enemies.ts';
import type { Tier } from './quality.ts';

/**
 * Draw one of everything, once.
 *
 * `renderer.compile` walks the *visible* graph, so anything pooled and hidden
 * — the shards, the spare bodies — has to be shown for the duration and put
 * back afterwards. They are parked far under the level rather than at the
 * origin, so that a compile which somehow lands on a real frame is a few
 * pixels in the floor rather than a body in the middle of the room.
 */
export function warmShaders(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
  tier: Tier,
): void {
  const parked = new THREE.Group();
  parked.position.set(0, -400, 0);

  // One hostile per distinct build, because the shell, the attachments, the
  // core and the telegraph are four different programs and a `hulk` and a
  // `drone` do not use the same set.
  const seen = new Set<string>();
  const bodies = ENEMY_TYPES.filter((t) => {
    const k = `${t.build}:${t.frontArmour ? 'shield' : ''}${t.lobs ? 'lob' : ''}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  })
    .slice(0, 6)
    .map((t) => makeBody(t, tier));
  for (const b of bodies) {
    parked.add(b.group, b.tell);
    b.tell.visible = true;
  }
  scene.add(parked);

  // Anything already in the scene but hidden — pooled shards, spare bodies —
  // has to be visible to be compiled, and exactly as it was afterwards.
  const hidden: THREE.Object3D[] = [];
  scene.traverse((o) => {
    if (!o.visible && (o as THREE.Mesh).isMesh) {
      hidden.push(o);
      o.visible = true;
    }
  });

  renderer.compile(scene, camera);

  for (const o of hidden) o.visible = false;
  scene.remove(parked);
  for (const b of bodies) {
    for (const m of [b.body, b.head, ...b.limbs, ...b.arms, b.eye, b.mark, b.tell]) {
      m.geometry.dispose?.();
      (m.material as THREE.Material).dispose?.();
    }
  }
}
